import { Order, Payment, ProductInterface } from '../../services/exports/Interfaces';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../../store/types';
import { CompleteBasketItem } from '../../providers/BasketProvider';
import ReactPixel from 'react-facebook-pixel';

export enum EVENTS {
  view_item = 'view_item',
  add_to_cart = 'add_to_cart',
  remove_from_cart = 'remove_from_cart',
  begin_checkout = 'begin_checkout',
  purchase = 'purchase',
}

export default function useAnalytics() {
  const { company } = useSelector((state: StoreInterface) => state.initialData);
  const { cached_order } = useSelector((state: StoreInterface) => state.order);

  const trackEvent = (event: EVENTS, data?: object) => {
    window.gtag('event', event, data);
    ReactPixel.track(event, data);
  };

  const trackViewItem = async (product: ProductInterface) =>
    new Promise((resolve) => {
      trackEvent(EVENTS.view_item, {
        currency: company?.currency,
        value: product?.actual_price,
        order_method: cached_order?.method,
        delivery_mode: company?.delivery_mode,
        items: [
          {
            item_id: product.id,
            item_name: product.name,
            price: product?.actual_price,
            location_id: company?.google_places_id,
          },
        ],
      });

      resolve(true);
    });

  const trackAddToCart = async (item: CompleteBasketItem) =>
    new Promise((resolve) => {
      trackEvent(EVENTS.add_to_cart, {
        currency: company?.currency,
        value: item?.actual_price,
        order_method: cached_order?.method,
        delivery_mode: company?.delivery_mode,
        items: [
          {
            item_id: item.product_id,
            item_name: item.entry?.name,
            price: item?.actual_price / item.quantity,
            location_id: company?.google_places_id,
          },
        ],
      });

      resolve(true);
    });

  const trackRemoveFromCart = async (item: CompleteBasketItem) =>
    new Promise((resolve) => {
      trackEvent(EVENTS.remove_from_cart, {
        currency: company?.currency,
        value: item?.actual_price,
        order_method: cached_order?.method,
        delivery_mode: company?.delivery_mode,
        items: [
          {
            item_id: item.product_id,
            item_name: item.entry?.name,
            price: item?.actual_price / item.quantity,
            location_id: company?.google_places_id,
          },
        ],
      });

      resolve(true);
    });

  const trackBeginCheckout = async (order: Order) =>
    new Promise((resolve) => {
      trackEvent(EVENTS.begin_checkout, {
        value: order.total,
        currency: company?.currency,
        order_method: order.method,
        delivery_mode: company?.delivery_mode,
        items: order.line_items.map((item, index) => ({
          item_id: item.product_id,
          item_name: item.name,
          price: item.actual_price / item.quantity,
          quantity: item.quantity,
          index,
          location_id: company?.google_places_id,
        })),
      });

      resolve(true);
    });

  const trackPurchase = async (payment: Payment, order: Order) =>
    new Promise((resolve) => {
      trackEvent(EVENTS.purchase, {
        transaction_id: payment.id,
        value: payment.amount,
        currency: company?.currency,
        order_method: order.method,
        delivery_mode: company?.delivery_mode,
        items: order.line_items.map((item, index) => ({
          item_id: item.product_id,
          item_name: item.name,
          price: item.actual_price / item.quantity,
          quantity: item.quantity,
          index,
          location_id: company?.google_places_id,
        })),
      });

      resolve(true);
    });

  return {
    trackEvent,
    trackViewItem,
    trackAddToCart,
    trackRemoveFromCart,
    trackBeginCheckout,
    trackPurchase,
  };
}
